import { useState, useEffect, useRef } from 'react';

interface UseScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
  staggerDelay?: number; // Delay in ms for staggered animations (for groups)
}

/**
 * Fade-in animation hook with Intersection Observer
 * 
 * Features:
 * - Fade in elements when they scroll into view
 * - Falls back gracefully if IntersectionObserver unavailable
 * - Configurable threshold and rootMargin
 * - Can trigger once or repeatedly
 * - GPU-accelerated animations
 * - Respects prefers-reduced-motion
 * 
 * @param options - Configuration options
 * @returns Animation state and ref
 */
export function useScrollAnimation({
  threshold = 0.1,
  rootMargin = '50px',
  triggerOnce = true,
  staggerDelay = 0
}: UseScrollAnimationOptions = {}) {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Progressive enhancement: only use IntersectionObserver if available
    if (typeof IntersectionObserver !== 'undefined' && elementRef.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            
            // Disconnect after first trigger if triggerOnce is true
            if (triggerOnce) {
              observer.disconnect();
            }
          } else if (!triggerOnce) {
            setIsVisible(false);
          }
        },
        { threshold, rootMargin }
      );
      
      observer.observe(elementRef.current);
      
      return () => {
        observer.disconnect();
      };
    } else {
      // Fallback for browsers without IntersectionObserver
      setIsVisible(true);
    }
    return undefined;
  }, [threshold, rootMargin, triggerOnce]);

  // Respect prefers-reduced-motion
  const shouldAnimate = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return {
    ref: elementRef,
    isVisible: shouldAnimate ? isVisible : true, // Always show if reduced motion
    style: {
      opacity: shouldAnimate && !isVisible ? 0 : 1,
      transform: shouldAnimate && !isVisible ? 'translateY(20px) translateZ(0)' : 'translateY(0) translateZ(0)',
      transition: shouldAnimate 
        ? `opacity 0.6s ease-out ${staggerDelay}ms, transform 0.6s ease-out ${staggerDelay}ms` 
        : 'none'
    }
  };
}
